// Helpers for the "Containers near me" ZIP search (no React, easy to test).
import { CONTAINER_CATEGORIES, sizeFromTitle } from '../StatePage/ctStatePages';

export const RADIUS_OPTIONS = [100, 250, 500];
export const DEFAULT_RADIUS = 250;

export const BUY_IT_NOW_LISTING_TYPES = ['container-trader-managed', 'dealer-buy-it-now'];

const SIZE_BY_LISTING_TYPE = {
  '20': "20' Standard",
  '20hc': "20' High Cube",
  '40': "40' Standard",
  '40hc': "40' High Cube",
  '45hc': "45' High Cube",
};

// Size filter for the ZIP search. Keys match the size listing types used in search links.
export const NEARBY_SIZES = Object.keys(SIZE_BY_LISTING_TYPE).map(id => ({
  id,
  label: SIZE_BY_LISTING_TYPE[id],
}));

export const NEARBY_TYPES = [
  { id: 'all', label: 'All containers' },
  { id: 'buy-it-now', label: 'Buy It Now only' },
];

export const parseNearbySearch = (search = '') => {
  const params = new URLSearchParams(search || '');
  const zip = (params.get('zip') || '').trim();
  const radius = parseInt(params.get('radius'), 10);
  const type = params.get('type') === 'buy-it-now' ? 'buy-it-now' : 'all';
  const size = params.get('size');
  return {
    zip: /^\d{5}$/.test(zip) ? zip : null,
    radius: RADIUS_OPTIONS.includes(radius) ? radius : DEFAULT_RADIUS,
    type,
    size: SIZE_BY_LISTING_TYPE[size] ? size : null,
  };
};

const EARTH_RADIUS_MILES = 3958.8;
const toRad = d => (d * Math.PI) / 180;

// Straight-line distance in miles. Road miles are usually somewhat longer.
export const milesBetween = (a, b) => {
  if (!a || !b) return null;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
};

// Rectangle that contains a circle of `radiusMiles` around the point.
export const boundsAround = ({ lat, lng }, radiusMiles) => {
  const dLat = radiusMiles / 69;
  const dLng = radiusMiles / (69 * Math.max(0.2, Math.cos(toRad(lat))));
  return {
    ne: { lat: Math.min(90, lat + dLat), lng: Math.min(180, lng + dLng) },
    sw: { lat: Math.max(-90, lat - dLat), lng: Math.max(-180, lng - dLng) },
  };
};

export const isContainerListing = l =>
  CONTAINER_CATEGORIES.includes(l?.attributes?.publicData?.categoryLevel1) ||
  BUY_IT_NOW_LISTING_TYPES.includes(l?.attributes?.publicData?.listingType);

export const isBuyItNowListing = l =>
  BUY_IT_NOW_LISTING_TYPES.includes(l?.attributes?.publicData?.listingType);

export const sizeOf = l =>
  SIZE_BY_LISTING_TYPE[(l?.attributes?.publicData?.listingType)] ||
  sizeFromTitle(l?.attributes?.title);

/**
 * Container listings within `radius` miles of `origin`, closest first, each with `miles`.
 */
export const rankByDistance = (listings, origin, { radius, type, size } = {}) =>
  (listings || [])
    .filter(isContainerListing)
    .filter(l => (type === 'buy-it-now' ? isBuyItNowListing(l) : true))
    .filter(l =>
      size && SIZE_BY_LISTING_TYPE[size] ? sizeOf(l) === SIZE_BY_LISTING_TYPE[size] : true
    )
    .map(l => {
      const g = l.attributes.geolocation;
      return { listing: l, miles: g ? milesBetween(origin, { lat: g.lat, lng: g.lng }) : null };
    })
    .filter(r => r.miles != null && (!radius || r.miles <= radius))
    .sort((a, b) => a.miles - b.miles);

export const formatMiles = miles =>
  miles == null ? '' : miles < 1 ? 'Under 1 mi' : `${Math.round(miles).toLocaleString('en-US')} mi`;
